import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'
import {
  BookkeepersManagementValueObjectsIncidenceRuleValidityEnum,
  PayrollSendToBookkeeperIncidenceFragment,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Loading from 'modules/core/components/Loading'
import { useCallback } from 'modules/core/hooks/useCallback'
import i18n from 'modules/core/lib/i18n'

import { UpdateResult } from 'modules/payroll_bundle/pages/IncidencesPage/components/_StatusChangeDialog/types'

import RuleEvaluationIncidenceTable from './_RuleEvaluationIncidenceTable'
import Stats from './_Stats'

interface Props {
  loadingRuleEvaluations: boolean
  results: UpdateResult | undefined
  ignoreWarnings: boolean | undefined
  setIgnoreWarnings: ((value: boolean) => void) | undefined
  incidences?: PayrollSendToBookkeeperIncidenceFragment[]
  filteredIncidenceIds: Set<number>
  syncing: boolean
}
const Content = ({
  loadingRuleEvaluations,
  results,
  ignoreWarnings,
  setIgnoreWarnings,
  incidences = [],
  filteredIncidenceIds,
  syncing,
}: Props) => {
  const invalidRuleEvaluations =
    !syncing &&
    filteredIncidenceIds.size > 0 &&
    incidences.some(
      (incidence) => incidence.ruleEvaluation && incidence.ruleEvaluation.validity !== 'valid'
    )

  const filterSendableIncidences = useCallback(() => {
    const acceptedStatuses: BookkeepersManagementValueObjectsIncidenceRuleValidityEnum[] = ['valid']
    if (ignoreWarnings) {
      acceptedStatuses.push('warning')
    }
    return (incidence: PayrollSendToBookkeeperIncidenceFragment) =>
      incidence.ruleEvaluation && acceptedStatuses.includes(incidence.ruleEvaluation.validity)
  }, [ignoreWarnings])

  const numIncidencesToBeSent = incidences.filter(filterSendableIncidences()).length

  return (
    <>
      {(loadingRuleEvaluations || syncing) && <Loading />}
      {!loadingRuleEvaluations &&
        !syncing &&
        (results?.success ? (
          <Text.Normal>{i18n.t('bookkeepers.incidence.bulk_status_changes.success')}</Text.Normal>
        ) : (
          <Box gap="s12">
            <Text.Normal>
              {i18n.t(
                'bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.to_send_message'
              )}
            </Text.Normal>
            {!loadingRuleEvaluations && invalidRuleEvaluations && (
              <>
                <Stats incidences={incidences} />
                <RuleEvaluationIncidenceTable incidences={incidences} />
              </>
            )}
            {numIncidencesToBeSent !== undefined && (
              <Box paddingTop="s8">
                <Callout
                  type="info"
                  compact
                  description={i18n.tp(
                    'bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.stats.to_send',
                    { count: numIncidencesToBeSent }
                  )}
                />
              </Box>
            )}
            {invalidRuleEvaluations && (
              <Checkbox
                label={i18n.t(
                  'bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.ignore_warnings'
                )}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setIgnoreWarnings && setIgnoreWarnings(event.target.checked)
                }
                checked={ignoreWarnings}
                disabled={incidences.every((rule) => rule.ruleEvaluation?.validity !== 'warning')}
              />
            )}
          </Box>
        ))}
    </>
  )
}

export default Content
